import "@/index.css";
import "@/buktu.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Home from "@/pages/Home";
import Solutions from "@/pages/Solutions";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import CoachDashboard from "@/pages/CoachDashboard";
import CoachOnboarding from "@/pages/CoachOnboarding";
import CoachSettings from "@/pages/CoachSettings";
import CoachReport from "@/pages/CoachReport";
import TeamJoin from "@/pages/TeamJoin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import { Toaster } from "@/components/ui/sonner";

function MarketingLayout() {
  return (
    <div className="buktu-root">
      <SiteNav />
      <main>
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="buktu-root" style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <p style={{ color: "var(--text-muted)" }}>Loading…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="buktu-root" style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <p style={{ color: "var(--text-muted)" }}>Loading…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function ImpersonationBanner() {
  const { user, setUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  if (!user || !user.impersonating) return null;
  const stop = async () => {
    try {
      const { data } = await api.post("/admin/stop-impersonate");
      setUser(data);
      navigate("/admin");
    } catch (e) {
      await refreshUser();
    }
  };
  return (
    <div data-testid="impersonation-banner" style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999, background: "#8a2a1a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 16, padding: "8px 16px", font: "var(--type-body-sm)", fontSize: 13 }}>
      <span>Viewing as <b>{user.name || user.email}</b>{user.impersonator_email ? ` · admin ${user.impersonator_email}` : ""}</span>
      <button data-testid="stop-impersonate-btn" onClick={stop} style={{ background: "#fff", color: "#8a2a1a", border: 0, borderRadius: 999, padding: "4px 14px", fontWeight: 700, cursor: "pointer" }}>Stop impersonating</button>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ImpersonationBanner />
        <Routes>
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/solutions" element={<Solutions />} />
            <Route path="/pricing" element={<Pricing />} />
          </Route>

          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/team/join" element={<TeamJoin />} />

          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
          <Route path="/coach" element={<ProtectedRoute><CoachDashboard /></ProtectedRoute>} />
          <Route path="/coach/onboarding" element={<ProtectedRoute><CoachOnboarding /></ProtectedRoute>} />
          <Route path="/coach/settings" element={<ProtectedRoute><CoachSettings /></ProtectedRoute>} />
          <Route path="/coach/report" element={<ProtectedRoute><CoachReport /></ProtectedRoute>} />

          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
